/**
 * LeadsTable - Componente para exibição de atendimentos em formato tabular
 * 
 * Este componente renderiza uma tabela de atendimentos/leads com colunas para
 * ID, WhatsAppId, ID de Atendimento no CRM, Data de Cadastro e Situação.
 * Também exibe um contador do total de atendimentos filtrados acima da tabela.
 */

import React, { useState } from 'react';

// Importação dos componentes do Material UI necessários para construção da tabela
import {
  Paper,          // Container com elevação para a tabela
  Table,          // Componente de tabela principal
  TableBody,      // Seção do corpo da tabela
  TableCell,      // Célula individual da tabela
  TableContainer, // Container com rolagem para a tabela
  TableHead,      // Seção do cabeçalho da tabela
  TableRow,       // Linha da tabela
  Typography,     // Componente de texto
  Box,            // Container flexível para layout
  Button,         // Botões para paginação
  CircularProgress, // Indicador de carregamento
  useMediaQuery,  // Hook para responsividade
  useTheme        // Hook para acessar o tema
} from '@mui/material';

// Importação do componente de linha personalizado para a tabela
import TableRowItem from './TableRowItem';

/**
 * Componente LeadsTable
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.atendimentos - Array de objetos contendo os dados de atendimentos a serem exibidos
 * @param {Function} props.onWhatsAppClick - Função de callback executada quando o usuário clica no WhatsAppId
 * @returns {JSX.Element} Tabela de atendimentos renderizada com contador
 */
export default function LeadsTable({ atendimentos, onWhatsAppClick }) {
  const [visibleItems, setVisibleItems] = useState(12);
  const [loadingAll, setLoadingAll] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Função para lidar com "Ver todos" com loading
  const handleViewAll = async () => {
    setLoadingAll(true);

    // Simula carregamento de todos os itens (substitua pela sua lógica real)
    await new Promise(resolve => setTimeout(resolve, 2000)); // 2 segundos de loading

    setVisibleItems(atendimentos.length);
    setLoadingAll(false);
  };

  return (
    <>
      {/* Header da tabela com informações */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: 'text.primary',
            fontSize: { xs: '1.125rem', md: '1.25rem' },
            mb: 1,
          }}
        >
          Lista de Atendimentos
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(0,0,0,0.6)',
            fontSize: { xs: '0.875rem', md: '0.9rem' },
            mb: 1,
          }}
        >
          Clique no WhatsAppId para visualizar detalhes do atendimento
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: '500',
            color: '#495057',
            fontSize: { xs: '0.9rem', md: '1rem' },
          }}
        >
          Total de atendimentos filtrados: {atendimentos.length}
        </Typography>
      </Box>

      {/* Tela de loading específica para "Ver todos" */}
      {loadingAll ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            py: 4,
            gap: 2,
            border: '1px solid rgba(0,0,0,0.05)',
            borderRadius: 3,
            bgcolor: '#fafafa',
          }}
        >
          <CircularProgress size={40} />
          <Typography sx={{ color: "#777", textAlign: "center" }}>
            Carregando todos os atendimentos...
          </Typography>
          <Typography sx={{ color: "#999", fontSize: "0.8rem", textAlign: "center" }}>
            Aguarde enquanto carregamos {atendimentos.length} atendimentos
          </Typography>
        </Box>
      ) : (
        <>
          {/* Container da tabela com estilização moderna */}
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.05)',
              overflow: isMobile ? 'auto' : 'hidden',
            }}
            aria-label="Tabela com lista de atendimentos filtrados"
          >
            <Table sx={{ minWidth: 800 }}>
              {/* Cabeçalho da tabela com gradiente sutil */}
              <TableHead>
                <TableRow
                  sx={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      color: '#495057',
                      letterSpacing: '0.5px',
                      py: 2,
                      borderBottom: '2px solid #dee2e6',
                    }
                  }}
                >
                  {/* Colunas do cabeçalho */}
                  <TableCell>ID</TableCell>
                  <TableCell>WhatsAppId</TableCell>
                  <TableCell>ID Atendimento CRM</TableCell>
                  <TableCell>Data de Cadastro</TableCell>
                  <TableCell>Situação</TableCell>
                </TableRow>
              </TableHead>
              
              {/* Corpo da tabela com linhas alternadas e hover effects */}
              <TableBody>
                {atendimentos.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{
                        textAlign: 'center',
                        py: 6,
                        color: 'rgba(0,0,0,0.6)',
                      }}
                    >
                      <Typography variant="body1">
                        Nenhum atendimento encontrado com os filtros aplicados
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  /* 
                    Mapeamento do array de atendimentos para componentes TableRowItem
                    Agora limitado pelos visibleItems
                  */
                  atendimentos.slice(0, visibleItems).map((item, index) => (
                    <TableRowItem
                      key={item.id} // Chave única necessária para elementos em listas no React
                      atendimento={item} // Dados do atendimento atual
                      onWhatsAppClick={onWhatsAppClick} // Função de callback para interação
                      index={index} // Índice para alternância de cores das linhas
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Botões de paginação - seguindo o mesmo padrão da sidebar */}
          {atendimentos.length > 12 && (
            <Box sx={{ 
              textAlign: 'center', 
              p: 2, 
              mt: 2,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>

                {/*Botão "Ver mais" versão Mobile*/}
                {(visibleItems < atendimentos.length && isMobile) && (
                  <Button
                    onClick={() => setVisibleItems(visibleItems + 12)}
                    variant="outlined"
                    size="small"
                    sx={{
                      px: 0.5, py: 0.5,
                      border: '1px solid #667eea',
                      borderRadius: '100%',
                      background: 'rgba(102, 126, 234, 0.08)',
                      color: '#667eea',
                      fontWeight: 1000,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      minWidth: '40px',
                      minHeight: '40px',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                        background: 'rgba(102, 126, 234, 0.12)',
                      }
                    }}
                  >
                    +
                  </Button>
                )}

                {/*Botão "Ver mais" versão Desktop*/}
                {(visibleItems < atendimentos.length && !isMobile) && (
                  <Button
                    onClick={() => setVisibleItems(visibleItems + 12)}
                    variant="outlined"
                    size="small"
                    sx={{
                      px: 2, py: 0.5,
                      border: '1px solid #667eea',
                      borderRadius: '16px',
                      background: 'rgba(102, 126, 234, 0.08)',
                      color: '#667eea',
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      minWidth: '80px',
                      minHeight: '28px',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                        background: 'rgba(102, 126, 234, 0.12)',
                      },
                      '&:disabled': {
                        opacity: 0.6,
                      }
                    }}
                  >
                    Ver mais
                  </Button>
                )}

                {/*Botão "Ver menos" versão Mobile*/}
                {(visibleItems > 12 && isMobile) && (
                  <Button
                    onClick={() => setVisibleItems(12)}
                    variant="outlined"
                    size="small"
                    sx={{
                      px: 0.5, py: 0.5,
                      border: '1px solid #e53935',
                      borderRadius: '100%',
                      background: 'rgba(229, 57, 53, 0.08)',
                      color: '#e53935',
                      fontWeight: 1000,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      minWidth: '40px',
                      minHeight: '40px',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(229, 57, 53, 0.2)',
                        background: 'rgba(229, 57, 53, 0.12)',
                      }
                    }}
                  >
                    −
                  </Button>
                )}

                {/*Botão "Ver menos" versão Desktop*/}
                {(visibleItems > 12 && !isMobile) && (
                  <Button
                    onClick={() => setVisibleItems(12)}
                    variant="outlined"
                    size="small"
                    sx={{
                      px: 2, py: 0.5,
                      border: '1px solid #e53935',
                      borderRadius: '16px',
                      background: 'rgba(229, 57, 53, 0.08)',
                      color: '#e53935',
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      minWidth: '80px',
                      minHeight: '28px',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(229, 57, 53, 0.2)',
                        background: 'rgba(229, 57, 53, 0.12)',
                      },
                      '&:disabled': {
                        opacity: 0.6,
                      }
                    }}
                  >
                    Ver menos
                  </Button>
                )}

                {/*Botão "Ver todos"*/}
                {visibleItems < atendimentos.length && (
                  <Button
                    onClick={handleViewAll}
                    variant="outlined"
                    size="small"
                    disabled={loadingAll}
                    sx={{
                      px: 2, py: 0.5,
                      border: '1px solid #43a047',
                      borderRadius: '16px',
                      background: 'rgba(76, 175, 80, 0.08)',
                      color: '#43a047',
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      minWidth: '80px',
                      minHeight: '28px',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)',
                        background: 'rgba(76, 175, 80, 0.12)',
                      },
                      '&:disabled': {
                        opacity: 0.6,
                      }
                    }}
                  >
                    {loadingAll ? 'Carregando...' : 'Ver todos'}
                  </Button>
                )}
              </Box>

              {/* Indicador de quantidade */}
              <Typography
                variant="caption"
                sx={{
                  color: '#777',
                  mt: 1,
                  display: 'block',
                  fontSize: '0.75rem'
                }}
              >
                Mostrando {Math.min(visibleItems, atendimentos.length)} de {atendimentos.length} atendimentos
              </Typography>
            </Box>
          )}
        </>
      )}
    </>
  );
}